import { useMemo, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMediaQuery } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CustomButton from '@/components/Button/button';
import { useInView } from '@/hooks/useInView';

import wineIllustration from '@/assets/CtaSection/wine.webp';
import sponsorLogo1 from '@/assets/CtaSection/logo1.svg';
import sponsorLogo2 from '@/assets/CtaSection/logo2.svg';
import sponsorLogo3 from '@/assets/CtaSection/logo3.svg';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

const SPONSOR_LOGOS = [
  { src: sponsorLogo1, alt: 'Sponsor logo 1', scale: 1.5 },
  { src: sponsorLogo2, alt: 'Sponsor logo 2', scale: 1.5 },
  { src: sponsorLogo3, alt: 'Sponsor logo 3', scale: 1.5 },
] as const;

const MARQUEE_CYCLES_PER_SEGMENT = 14;

const MARQUEE_DURATION_S = 100;

function buildMarqueeTrack(): (typeof SPONSOR_LOGOS)[number][] {
  const len = SPONSOR_LOGOS.length;
  const segment = Array.from(
    { length: MARQUEE_CYCLES_PER_SEGMENT * len },
    (_, i) => {
      const logo = SPONSOR_LOGOS[i % len];
      if (!logo) {
        throw new Error('Sponsor logo index out of range');
      }
      return logo;
    }
  );
  return [...segment, ...segment];
}

const CONTACT_EMAIL = 'hello@ukvineyardguide.co.uk';
const CONTACT_SUBJECT = 'Partnership Inquiry - UK Vineyard Guide';
const CONTACT_BODY =
  "Hi,\n\nI'm interested in working with UK Vineyard Guide.\n\n";

export default function CtaSection(): JSX.Element {
  const [, setShowFallback] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [sectionRef, inView] = useInView({
    threshold: 0.3,
    rootMargin: '0px 0px -80px 0px',
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'CTA / Partners Section',
      });
    }
  }, [inView]);
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)'
  );
  const marqueeItems = useMemo(
    () => (prefersReducedMotion ? [...SPONSOR_LOGOS] : buildMarqueeTrack()),
    [prefersReducedMotion]
  );

  const handleContactClick = (): void => {
    gtag('event', 'cta_click', {
      event_category: 'Engagement',
      event_label: 'Find out more – Partners',
    });
    const mailtoLink = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}&body=${encodeURIComponent(CONTACT_BODY)}`;
    const startTime = Date.now();
    window.location.href = mailtoLink;

    // If no email app opens after 1 second, show fallback
    setTimeout(() => {
      if (Date.now() - startTime < 1500) {
        setShowFallback(true);
      }
    }, 1000);
  };

  // const handleCopyEmail = (): void => {
  //   navigator.clipboard
  //     .writeText(CONTACT_EMAIL)
  //     .then(() => {
  //       setCopiedEmail(true);
  //       setTimeout(() => setCopiedEmail(false), 2000);
  //     })
  //     .catch(() => {
  //       const input = document.createElement('input');
  //       input.value = CONTACT_EMAIL;
  //       document.body.appendChild(input);
  //       input.select();
  //       document.execCommand('copy');
  //       document.body.removeChild(input);
  //       setCopiedEmail(true);
  //       setTimeout(() => setCopiedEmail(false), 2000);
  //     });
  // };

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="partners"
      sx={{
        backgroundColor: '#D9D2C4',
        py: { xs: '48px', md: '64px' },
        px: { xs: '24px', md: '64px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          gap: { xs: '32px', md: '48px' },
          mb: { xs: '56px', md: '64px' },
        }}
      >
        <Box
          sx={{
            flex: '1 1 50%',
            maxWidth: { md: '800px' },
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...typography.display,
              display: 'block',
              width: { xs: '100%', md: 'auto' },
              color: color.charcoal.default,
              mb: '20px',
              fontSize: {
                xs: 'var(--font-size-36, 36px)',
                sm: 'var(--font-size-48, 48px)',
                md: typography.display.fontSize,
              },
            }}
          >
            Partners
          </Typography>
          <Typography
            sx={{
              ...typography.heading4,
              display: 'block',
              width: { xs: '100%', md: 'auto' },
              color: color.charcoal.default,
              fontWeight: 350,
              mb: '16px',
              fontSize: {
                xs: 'var(--font-size-16, 16px)',
                sm: 'var(--font-size-18, 18px)',
                md: typography.heading5.fontSize,
              },
            }}
          >
            We are currently building our network and inviting producers across
            England and Wales – with or without cellar doors – to join the
            platform.
          </Typography>
          <Typography
            sx={{
              ...typography.heading4,
              display: 'block',
              width: { xs: '100%', md: 'auto' },
              color: color.charcoal.default,
              fontWeight: 350,
              mb: '24px',
              fontSize: {
                xs: 'var(--font-size-16, 16px)',
                sm: 'var(--font-size-18, 18px)',
                md: typography.heading5.fontSize,
              },
            }}
          >
            If you&apos;re a wine producer, estate, or wine-focused business
            interested in being featured, we&apos;d love to hear from you.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <CustomButton
              variant="contained"
              onClick={handleContactClick}
              padding="14px 28px"
            >
              Find out more
            </CustomButton>
          </Box>
        </Box>
        <Box
          sx={{
            flex: '0 1 auto',
            alignSelf: { xs: 'center', md: 'auto' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: { xs: '280px', md: '380px' },
            width: '100%',
            opacity: 0.92,
          }}
        >
          <Box
            component="img"
            src={wineIllustration}
            alt=""
            aria-hidden
            loading="lazy"
            sx={{
              width: '100%',
              height: '313px',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            ...typography.display,
            color: color.charcoal.default,
            mb: { xs: '20px', md: '32px' },
            fontSize: {
              xs: 'var(--font-size-36, 36px)',
              sm: 'var(--font-size-48, 48px)',
              md: typography.display.fontSize,
            },
          }}
        >
          Cheers to our sponsors...
        </Typography>
        <Box
          role="region"
          aria-label="Sponsor logos"
          sx={{
            width: '100%',
            overflow: 'hidden',
            maskImage:
              'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          }}
        >
          <Box
            component="ul"
            sx={{
              display: 'flex',
              width: prefersReducedMotion ? '100%' : 'max-content',
              m: 0,
              p: 0,
              listStyle: 'none',
              ...(prefersReducedMotion
                ? {
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }
                : {
                    '@keyframes sponsorMarquee': {
                      '0%': { transform: 'translateX(0)' },
                      '100%': { transform: 'translateX(-50%)' },
                    },
                    animation: `sponsorMarquee ${MARQUEE_DURATION_S}s linear infinite`,
                  }),
            }}
          >
            {marqueeItems.map((logo, index) => (
              <Box
                key={`${logo.src}-${index}`}
                component="li"
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: { xs: '28px', md: '40px' },
                  py: '8px',
                }}
              >
                <Box
                  component="img"
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  sx={{
                    height: { xs: 36, md: 44 },
                    width: 'auto',
                    maxWidth: { xs: 140, md: 180 },
                    objectFit: 'contain',
                    display: 'block',
                    transform: `scale(${logo.scale})`,
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Fallback snackbar when no email client is detected */}
      {/* <Snackbar
        open={showFallback}
        autoHideDuration={6000}
        onClose={() => setShowFallback(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setShowFallback(false)}
          icon={false}
          sx={{
            width: '100%',
            backgroundColor: color.terracotta.default,
            color: 'white',
          }}
        >
          No email app configured. Copy this address:{' '}
          <button
            type="button"
            onClick={handleCopyEmail}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              background: 'none',
              border: 0,
              padding: 0,
              margin: 0,
              color: 'inherit',
              font: 'inherit',
            }}
          >
            {CONTACT_EMAIL}
          </button>
        </Alert>
      </Snackbar> */}

      {/* Snackbar for email copied confirmation */}
      <Snackbar
        open={copiedEmail}
        autoHideDuration={2000}
        onClose={() => setCopiedEmail(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setCopiedEmail(false)}
          sx={{
            width: '100%',
            backgroundColor: color.terracotta.default,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          Email copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
}
